/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Widget from './Widget';
import { getAIBalance, getAISpendingSummary } from '../../api/billing';

/**
 * AICreditsWidget - Compact credits balance + usage overview for the dashboard.
 */
const AICreditsWidget = ({ hideTitle = false }) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [balRes, sumRes] = await Promise.all([
        getAIBalance(),
        getAISpendingSummary(7),
      ]);
      setBalance(balRes?.data ?? balRes);
      setSummary(sumRes?.data ?? sumRes);
    } catch (err) {
      console.error('AICreditsWidget fetch error:', err);
      setError('Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60_000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const fmt = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '—';

  const balanceColor = () => {
    if (!balance) return 'text-textPrimary';
    if (balance.is_zero_balance) return 'text-destructive';
    if (balance.is_low_balance) return 'text-yellow-400';
    return 'text-success';
  };

  return (
    <Widget title="Credits" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {loading && !balance ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">Loading…</div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <span className="text-destructive text-sm">{error}</span>
            <button className="btn btn-neutral btn-xs" onClick={fetchData}>Retry</button>
          </div>
        ) : (
          <>
            {/* Balance */}
            <div className="px-3 py-3 border-b border-borderDefault">
              <div className="text-xs text-textSecondary mb-1">Credit Balance</div>
              <div className={`text-2xl font-bold ${balanceColor()}`}>{fmt(balance?.balance)} cr</div>
              {balance?.auto_pause_on_zero && balance?.is_zero_balance && (
                <div className="text-xs text-destructive mt-1">AI paused — zero balance</div>
              )}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-px bg-borderDefault flex-1">
              <div className="bg-backgroundSurface px-3 py-2.5">
                <div className="text-[10px] text-textSecondary">7-Day Usage</div>
                <div className="text-sm font-semibold text-textPrimary">
                  {fmt(summary?.period_spent)} cr
                </div>
              </div>
              <div className="bg-backgroundSurface px-3 py-2.5">
                <div className="text-[10px] text-textSecondary">Today</div>
                <div className="text-sm font-semibold text-textPrimary">
                  {fmt(summary?.today_spent)} cr
                </div>
              </div>
              <div className="bg-backgroundSurface px-3 py-2.5">
                <div className="text-[10px] text-textSecondary">Daily Limit</div>
                <div className="text-sm font-semibold text-textPrimary">
                  {balance?.daily_limit ? `${fmt(balance.daily_limit)} cr` : 'None'}
                </div>
              </div>
              <div className="bg-backgroundSurface px-3 py-2.5">
                <div className="text-[10px] text-textSecondary">Lifetime Used</div>
                <div className="text-sm font-semibold text-textPrimary">
                  {fmt(balance?.lifetime_debits)} cr
                </div>
              </div>
            </div>

            {/* Link to credits */}
            <div className="px-3 py-2 border-t border-borderDefault flex items-center justify-between">
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate('/billing?tab=credits')}
              >
                Manage Credits →
              </button>
              <button
                className="btn btn-primary btn-xs"
                onClick={() => navigate('/billing?tab=credits')}
              >
                Top Up
              </button>
            </div>
          </>
        )}
      </div>
    </Widget>
  );
};

export default AICreditsWidget;
